import api from '../axios';

// Deep Search Request/Response Types based on backend API
export interface DeepSearchDto {
  brandId: string;
  userId: string;
  searchQuery: string;
  file?: string;
  isWebSearch: boolean;
  historyId?: string;
}

export interface UserSearchHistory {
  id: string;
  brandId: string;
  userId: string;
  query: string;
  isWebSearch: boolean;
  file?: string;
  result: any;
  createdAt: string;
  updatedAt: string;
}

export interface SearchStatusDto {
  id: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'SUCCESS';
  progress?: number;
  message?: string;
  result?: any;
}

export interface WebSearchResult {
  type: 'web_search';
  query: string;
  results: {
    totalResults: number;
    searchTime: string;
    items: Array<{
      title: string;
      url: string;
      snippet: string;
      displayUrl: string;
    }>;
  };
  timestamp: string;
}

export interface FileSearchResult {
  type: 'file_search';
  query: string;
  file: string;
  results: {
    totalMatches: number;
    matches: Array<{
      lineNumber: number;
      content: string;
      context: string;
    }>;
  };
  timestamp: string;
}

// Deep Search Service Class
export class DeepSearchService {
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Core Search Operations
  async performSearch(searchData: DeepSearchDto): Promise<UserSearchHistory> {
    const response = await api.post<UserSearchHistory>('/deep-search/search', searchData);
    return response.data;
  }

  async performWebSearch(
    brandId: string,
    userId: string,
    searchQuery: string,
    historyId?: string
  ): Promise<UserSearchHistory> {
    return this.performSearch({
      brandId,
      userId,
      searchQuery,
      isWebSearch: true,
      historyId,
    });
  }

  async performFileSearch(
    brandId: string,
    userId: string,
    searchQuery: string,
    file: string,
    historyId?: string
  ): Promise<UserSearchHistory> {
    return this.performSearch({
      brandId,
      userId,
      searchQuery,
      file,
      isWebSearch: false,
      historyId,
    });
  }

  // Search History Management
  async getSearchHistory(userId: string, brandId: string): Promise<UserSearchHistory[]> {
    const queryParams = new URLSearchParams({
      userId,
      brandId,
    });

    const response = await api.get<UserSearchHistory[]>(
      `/deep-search/history?${queryParams.toString()}`
    );
    return response.data;
  }

  async getSearchById(
    searchId: string,
    userId: string,
    brandId: string
  ): Promise<UserSearchHistory> {
    const queryParams = new URLSearchParams({
      userId,
      brandId,
    });

    const response = await api.get<UserSearchHistory>(
      `/deep-search/${searchId}?${queryParams.toString()}`
    );
    return response.data;
  }

  // Update existing search
  async updateSearch(
    searchId: string,
    userId: string,
    brandId: string,
    updateData: {
      searchQuery?: string;
      file?: string;
      isWebSearch?: boolean;
      result?: any;
      responseStatus?: 'PENDING' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILURE' | 'PARTIAL';
    }
  ): Promise<UserSearchHistory> {
    const queryParams = new URLSearchParams({
      userId,
      brandId,
    });

    const response = await api.put<UserSearchHistory>(
      `/deep-search/${searchId}?${queryParams.toString()}`,
      updateData
    );
    return response.data;
  }

  // Search Status and Progress
  async getSearchStatus(
    searchId: string,
    userId: string,
    brandId: string
  ): Promise<SearchStatusDto> {
    const queryParams = new URLSearchParams({
      userId,
      brandId,
    });

    const response = await api.get<SearchStatusDto>(
      `/deep-search/${searchId}/status?${queryParams.toString()}`
    );
    return response.data;
  }

  // Utility Methods for Search Management
  async searchAndWaitForCompletion(
    searchData: DeepSearchDto,
    options?: {
      maxPollingTime?: number; // milliseconds
      pollingInterval?: number; // milliseconds
      onProgress?: (status: SearchStatusDto) => void;
    }
  ): Promise<UserSearchHistory> {
    const maxPollingTime = options?.maxPollingTime || 30000; // 30 seconds
    const pollingInterval = options?.pollingInterval || 1000; // 1 second
    const startTime = Date.now();

    // Start the search
    const searchResult = await this.performSearch(searchData);
    
    // Poll for completion
    while (Date.now() - startTime < maxPollingTime) {
      const status = await this.getSearchStatus(
        searchResult.id,
        searchData.userId,
        searchData.brandId
      );

      if (options?.onProgress) {
        options.onProgress(status);
      }

      if (status.status === 'SUCCESS') {
        return this.getSearchById(searchResult.id, searchData.userId, searchData.brandId);
      }

      if (status.status !== 'PENDING' && status.status !== 'IN_PROGRESS') {
        throw new Error(status.message || 'Search failed');
      }

      await this.delay(pollingInterval);
    }

    throw new Error('Search timeout - operation took too long');
  }

  // Convenience methods for different search types
  async quickWebSearch(
    brandId: string,
    userId: string,
    query: string
  ): Promise<WebSearchResult | null> {
    try {
      const result = await this.searchAndWaitForCompletion({
        brandId,
        userId,
        searchQuery: query,
        isWebSearch: true,
      });

      return result.result as WebSearchResult;
    } catch (error) {
      console.error('Quick web search failed:', error);
      return null;
    }
  }

  async quickFileSearch(
    brandId: string,
    userId: string,
    query: string,
    file: string
  ): Promise<FileSearchResult | null> {
    try {
      const result = await this.searchAndWaitForCompletion({
        brandId,
        userId,
        searchQuery: query,
        file,
        isWebSearch: false,
      });

      return result.result as FileSearchResult;
    } catch (error) {
      console.error('Quick file search failed:', error);
      return null;
    }
  }

  // Search History Filtering and Utilities
  async getWebSearchHistory(userId: string, brandId: string): Promise<UserSearchHistory[]> {
    const history = await this.getSearchHistory(userId, brandId);
    return history.filter(search => search.isWebSearch);
  }

  async getFileSearchHistory(userId: string, brandId: string): Promise<UserSearchHistory[]> {
    const history = await this.getSearchHistory(userId, brandId);
    return history.filter(search => !search.isWebSearch);
  }

  async getSearchHistoryByQuery(
    userId: string,
    brandId: string,
    queryFilter: string
  ): Promise<UserSearchHistory[]> {
    const history = await this.getSearchHistory(userId, brandId);
    return history.filter(search => 
      search.query.toLowerCase().includes(queryFilter.toLowerCase())
    );
  }

  // Advanced Search Operations
  async batchSearch(
    searches: DeepSearchDto[],
    options?: {
      concurrent?: boolean;
      maxConcurrency?: number;
    }
  ): Promise<UserSearchHistory[]> {
    const concurrent = options?.concurrent ?? false;
    const maxConcurrency = options?.maxConcurrency ?? 3;

    if (concurrent) {
      // Process searches concurrently with limited concurrency
      const results: UserSearchHistory[] = [];
      const chunks = this.chunkArray(searches, maxConcurrency);

      for (const chunk of chunks) {
        const chunkResults = await Promise.all(
          chunk.map(search => this.performSearch(search))
        );
        results.push(...chunkResults);
      }

      return results;
    } else {
      // Process searches sequentially
      const results: UserSearchHistory[] = [];
      for (const search of searches) {
        const result = await this.performSearch(search);
        results.push(result);
      }
      return results;
    }
  }

  // Helper method to chunk array for concurrent processing
  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  // Search Analytics and Insights
  async getSearchAnalytics(userId: string, brandId: string): Promise<{
    totalSearches: number;
    webSearches: number;
    fileSearches: number;
    averageExecutionTime: number;
    mostSearchedQueries: Array<{ query: string; count: number }>;
    recentActivity: UserSearchHistory[];
  }> {
    const history = await this.getSearchHistory(userId, brandId);
    
    const webSearches = history.filter(s => s.isWebSearch).length;
    const fileSearches = history.filter(s => !s.isWebSearch).length;
    
    // Group queries and count occurrences
    const queryCount = history.reduce((acc, search) => {
      acc[search.query] = (acc[search.query] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const mostSearchedQueries = Object.entries(queryCount)
      .map(([query, count]) => ({ query, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    const recentActivity = history.slice(0, 5);

    return {
      totalSearches: history.length,
      webSearches,
      fileSearches,
      averageExecutionTime: 0, // Would need execution time data from backend
      mostSearchedQueries,
      recentActivity,
    };
  }

  // Validation helpers
  validateSearchInput(searchData: Partial<DeepSearchDto>): string[] {
    const errors: string[] = [];

    if (!searchData.brandId) errors.push('Brand ID is required');
    if (!searchData.userId) errors.push('User ID is required');
    if (!searchData.searchQuery?.trim()) errors.push('Search query is required');
    if (searchData.isWebSearch === undefined) errors.push('Search type (web/file) must be specified');
    if (!searchData.isWebSearch && !searchData.file?.trim()) {
      errors.push('File name is required for file searches');
    }

    return errors;
  }

  isValidUUID(id: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  }
}

// Create and export singleton instance
export const deepSearchService = new DeepSearchService();

// Export individual methods for convenience
export const {
  performSearch,
  performWebSearch,
  performFileSearch,
  getSearchHistory,
  getSearchById,
  updateSearch,
  getSearchStatus,
  searchAndWaitForCompletion,
  quickWebSearch,
  quickFileSearch,
  getWebSearchHistory,
  getFileSearchHistory,
  getSearchHistoryByQuery,
  batchSearch,
  getSearchAnalytics,
  validateSearchInput,
  isValidUUID
} = deepSearchService;
